import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Hero3D from "../components/Hero3D";
import PricingCard from "../components/PricingCard";
import { Server, Cloud, Globe, Shield, Mail, Zap, ArrowRight, Star, CheckCircle, Award, Users, Cpu, HardDrive } from "lucide-react";
import { ComposableMap, Geographies, Geography, Marker, Line } from "react-simple-maps";

const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const services = [
  { icon: Server, label: "VPS Hosting", desc: "Full root access. Dedicated resources.", href: "/vps-hosting", color: "#00F3FF", from: "₹599/mo" },
  { icon: Cloud, label: "Cloud Hosting", desc: "20x power. Managed & scalable.", href: "/cloud-hosting", color: "#BC13FE", from: "₹549/mo" },
  { icon: Globe, label: "Shared Hosting", desc: "Perfect for beginners & blogs.", href: "/shared-hosting", color: "#00FF94", from: "₹69/mo" },
  { icon: Zap, label: "GPU Cloud", desc: "Nvidia H200 & A6000 AI Instances.", href: "/gpu-cloud", color: "#ffcc00", from: "₹130/hr" },
  { icon: Globe, label: "Domain Names", desc: ".in, .com, .ai & 50+ more.", href: "/domains", color: "#00F3FF", from: "₹299/yr" },
  { icon: Shield, label: "SSL Certificates", desc: "Secure your website today.", href: "/ssl", color: "#BC13FE", from: "₹199/yr" },
  { icon: Mail, label: "Email Hosting", desc: "Professional business email.", href: "/email-hosting", color: "#00FF94", from: "₹79/mo" },
];

const features = [
  { icon: Cpu, title: "AMD EPYC Processors", desc: "Latest gen CPUs for blazing performance" },
  { icon: HardDrive, title: "NVMe SSD Storage", desc: "10x faster than traditional SSDs" },
  { icon: Shield, title: "DDoS Protection", desc: "Enterprise-grade Wanguard filtering" },
  { icon: Zap, title: "99.9% Uptime SLA", desc: "Guaranteed reliability for your business" },
  { icon: Globe, title: "10+ Datacenters", desc: "India + global network for speed" },
  { icon: CheckCircle, title: "24/7 Human Support", desc: "Our experts answer in under 2 minutes" },
];

const testimonials = [
  { name: "Rahul Sharma", role: "AI Startup Founder", text: "IndiaCloud's H200 instances are unmatched. Our model training is 3x faster now and the pricing is incredible for what you get.", rating: 5 },
  { name: "Priya Mehta", role: "Machine Learning Engineer", text: "The A6000 Pro cloud is a game-changer. Instant deployments, high vRAM, and the Human Support 24x7 team is super responsive.", rating: 5 },
  { name: "Arjun Nair", role: "SaaS Platform Owner", text: "Migrated our inference workloads from AWS and the India datacenter makes a huge difference. Reduced our operational GPU costs dramatically.", rating: 5 },
];

function CounterStat({ value, label }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const numeric = parseInt(value.replace(/[^0-9]/g, ""));
        const duration = 1800;
        const step = Math.ceil(numeric / (duration / 16));
        let current = 0;
        const timer = setInterval(() => {
          current += step;
          if (current >= numeric) { setCount(numeric); clearInterval(timer); }
          else setCount(current);
        }, 16);
      }
    });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);

  return (
    <div ref={ref} className="text-center">
      <div className="font-orbitron font-900 text-3xl lg:text-4xl text-gradient mb-1">
        {value.includes("K") ? `${count}K+` : value.includes("%") ? `${count}%` : `${count}+`}
      </div>
      <div className="font-outfit text-sm text-gray-500">{label}</div>
    </div>
  );
}

export default function Home() {
  const [popularPlans, setPopularPlans] = useState([]);
  const [billingPeriod, setBillingPeriod] = useState("monthly");

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const gpu = await axios.get(`${API}/plans/category/gpu`);
        setPopularPlans(gpu.data.slice(0, 3));
      } catch (e) {
        console.error(e);
      }
    };
    fetchPlans();
  }, []);

  return (
    <div data-testid="home-page" className="page-enter">
      {/* Hero */}
      <Hero3D />

      {/* Stats */}
      <section className="py-16 border-y" style={{ borderColor: "rgba(0,243,255,0.06)", background: "rgba(10,10,18,0.5)" }}>
        <div className="max-w-5xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
          <CounterStat value="50K" label="Active Customers" />
          <CounterStat value="99.9%" label="Uptime Guarantee" />
          <CounterStat value="10" label="Global Datacenters" />
          <CounterStat value="4" label="Min Support Response" />
        </div>
      </section>

      {/* Global Infrastructure Map */}
      <section className="py-20 overflow-hidden relative" style={{ background: "#030305" }}>
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 50%, rgba(0,243,255,0.05) 0%, transparent 70%)" }} />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-full text-xs font-rajdhani font-700 tracking-widest"
              style={{ background: "rgba(0,243,255,0.08)", border: "1px solid rgba(0,243,255,0.2)", color: "#00F3FF" }}>
              <Globe size={12} /> GLOBAL NETWORK
            </div>
            <h2 className="font-orbitron font-700 text-3xl lg:text-5xl text-white mb-4">
              Infrastructure Without <span className="text-gradient">Borders.</span>
            </h2>
            <p className="font-outfit text-base text-gray-500 max-w-2xl mx-auto" style={{ fontWeight: 300 }}>
              Deploy workloads across our massive edge network. We bring your data closer to your users 
              for sub-millisecond latency worldwide.
            </p>
          </div>

          <div className="relative w-full max-w-5xl mx-auto rounded-3xl overflow-hidden glass p-4" style={{ border: "1px solid rgba(0,243,255,0.15)", background: "rgba(10,10,18,0.6)" }}>
            <ComposableMap projectionConfig={{ center: [78, 20], scale: 220 }} style={{ width: "100%", height: "auto" }}>
              <Geographies geography={geoUrl}>
                {({ geographies }) =>
                  geographies.map((geo) => (
                    <Geography key={geo.rsmKey} geography={geo} fill="rgba(255,255,255,0.05)" stroke="rgba(0,243,255,0.15)" strokeWidth={0.5} />
                  ))
                }
              </Geographies>
              
              {/* Connecting Global Network Lines - Sea Connect Fibre */}
              <Line from={[72.8777, 19.0760]} to={[77.1025, 28.7041]} stroke="#BC13FE" strokeWidth={1.5} strokeDasharray="3,3" className="animate-pulse opacity-80" />
              <Line from={[72.8777, 19.0760]} to={[103.8198, 1.3521]} stroke="#BC13FE" strokeWidth={1.5} strokeDasharray="3,3" className="animate-pulse opacity-80" />
              <Line from={[72.8777, 19.0760]} to={[8.6821, 50.1109]} stroke="#00F3FF" strokeWidth={1.5} strokeDasharray="3,3" className="animate-pulse opacity-80" />
              <Line from={[8.6821, 50.1109]} to={[-74.0060, 40.7128]} stroke="#BC13FE" strokeWidth={1.5} strokeDasharray="3,3" className="animate-pulse opacity-80" />

              {/* Server Nodes */}
              {[
                { name: "Mumbai, IN", coordinates: [72.8777, 19.0760] },
                { name: "Delhi, IN", coordinates: [77.1025, 28.7041] },
                { name: "Frankfurt, DE", coordinates: [8.6821, 50.1109] },
                { name: "New York, US", coordinates: [-74.0060, 40.7128] },
                { name: "Singapore, SG", coordinates: [103.8198, 1.3521] },
              ].map(({ name, coordinates }) => (
                <Marker key={name} coordinates={coordinates}>
                  <g className="group cursor-pointer">
                    <g transform="translate(-6, -6)">
                      <Server size={12} color="#00F3FF" className="animate-pulse" style={{ filter: "drop-shadow(0 0 10px #00F3FF)" }} />
                    </g>
                    <rect x={-40} y={-26} width={80} height={18} rx={4} fill="rgba(10,10,18,0.95)" stroke="#00F3FF" strokeWidth={0.5} 
                          className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                    <text textAnchor="middle" y={-14} style={{ fontFamily: "Rajdhani, sans-serif", fill: "#00F3FF", fontSize: "9px", fontWeight: "700" }} 
                          className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none tracking-wider">
                      {name.toUpperCase()}
                    </text>
                  </g>
                </Marker>
              ))}
            </ComposableMap>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 section-bg">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-full text-xs font-rajdhani font-700 tracking-widest"
              style={{ background: "rgba(188,19,254,0.08)", border: "1px solid rgba(188,19,254,0.2)", color: "#BC13FE" }}>
              HOSTING SOLUTIONS
            </div>
            <h2 className="font-orbitron font-700 text-3xl lg:text-4xl text-white mb-4">
              Everything You Need to
              <span className="text-gradient"> Train & Scale</span>
            </h2>
            <p className="font-outfit text-base text-gray-500 max-w-2xl mx-auto" style={{ fontWeight: 300 }}>
              From scalable Cloud CPU servers to ultra-powerful AI GPU infrastructure — IndiaCloud has the perfect compute solution.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {services.map((svc) => (
              <Link
                key={svc.href}
                to={svc.href}
                data-testid={`service-card-${svc.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="no-underline group"
              >
                <div
                  className="glass rounded-2xl p-5 flex flex-col gap-3 transition-all duration-300 group-hover:-translate-y-2 cursor-pointer h-full"
                  style={{ border: `1px solid rgba(${svc.color === "#00F3FF" ? "0,243,255" : svc.color === "#BC13FE" ? "188,19,254" : svc.color === "#00FF94" ? "0,255,148" : "255,204,0"},0.12)` }}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ background: `${svc.color}15`, border: `1px solid ${svc.color}30` }}
                  >
                    <svc.icon size={18} color={svc.color} />
                  </div>
                  <div>
                    <h3 className="font-rajdhani font-700 text-sm text-white mb-1">{svc.label}</h3>
                    <p className="font-outfit text-xs text-gray-600" style={{ fontWeight: 300 }}>{svc.desc}</p>
                  </div>
                  <div className="mt-auto font-orbitron text-xs" style={{ color: svc.color }}>
                    From {svc.from}
                  </div>
                </div>
              </Link>
            ))}

            {/* AI Recommender CTA */}
            <Link to="/plan-recommender" className="no-underline group">
              <div
                className="rounded-2xl p-5 flex flex-col gap-3 transition-all duration-300 group-hover:-translate-y-2 cursor-pointer h-full"
                style={{
                  background: "linear-gradient(135deg, rgba(0,243,255,0.08), rgba(188,19,254,0.08))",
                  border: "1px solid rgba(0,243,255,0.2)",
                }}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: "linear-gradient(135deg, rgba(0,243,255,0.2), rgba(188,19,254,0.2))", border: "1px solid rgba(0,243,255,0.3)" }}>
                  <Zap size={18} color="#00F3FF" />
                </div>
                <div>
                  <h3 className="font-rajdhani font-700 text-sm text-white mb-1">AI Plan Picker</h3>
                  <p className="font-outfit text-xs text-gray-600" style={{ fontWeight: 300 }}>Not sure? Let AI choose for you.</p>
                </div>
                <div className="mt-auto font-rajdhani text-xs font-700 flex items-center gap-1" style={{ color: "#00F3FF" }}>
                  TRY IT FREE <ArrowRight size={10} />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Popular Plans */}
      {popularPlans.length > 0 && (
        <section data-testid="popular-plans-section" className="py-20" style={{ background: "rgba(10,10,18,0.3)" }}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-full text-xs font-rajdhani font-700 tracking-widest"
                style={{ background: "rgba(0,255,148,0.08)", border: "1px solid rgba(0,255,148,0.2)", color: "#00FF94" }}>
                <Award size={12} /> MOST POPULAR GPU INSTANCES
              </div>
              <h2 className="font-orbitron font-700 text-3xl lg:text-4xl text-white mb-4">
                Train on H200. <span className="text-gradient">Scale on A6000.</span>
              </h2>

              {/* Billing Toggle */}
              <div data-testid="home-billing-toggle" className="inline-flex items-center gap-1 p-1 rounded-full mt-4"
                style={{ background: "rgba(10,10,18,0.9)", border: "1px solid rgba(0,243,255,0.15)" }}>
                <button
                  data-testid="home-billing-monthly"
                  onClick={() => setBillingPeriod("monthly")}
                  className="px-5 py-2 rounded-full text-xs font-rajdhani font-700 tracking-wider transition-all duration-300"
                  style={{
                    background: billingPeriod === "monthly" ? "linear-gradient(135deg, #00F3FF, #BC13FE)" : "transparent",
                    color: billingPeriod === "monthly" ? "#030305" : "#9ca3af",
                  }}
                >
                  MONTHLY
                </button>
                <button
                  data-testid="home-billing-yearly"
                  onClick={() => setBillingPeriod("yearly")}
                  className="px-5 py-2 rounded-full text-xs font-rajdhani font-700 tracking-wider transition-all duration-300 flex items-center gap-1.5"
                  style={{
                    background: billingPeriod === "yearly" ? "linear-gradient(135deg, #00F3FF, #BC13FE)" : "transparent",
                    color: billingPeriod === "yearly" ? "#030305" : "#9ca3af",
                  }}
                >
                  YEARLY
                  <span className="text-xs px-1.5 py-0.5 rounded-md font-600"
                    style={{
                      background: billingPeriod === "yearly" ? "rgba(0,0,0,0.2)" : "rgba(0,255,148,0.15)",
                      color: billingPeriod === "yearly" ? "#030305" : "#00FF94",
                      fontSize: "9px",
                    }}>
                    -15%
                  </span>
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {popularPlans.map((plan) => (
                <PricingCard key={plan.id} plan={plan} billingPeriod={billingPeriod} />
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/gpu-cloud" className="no-underline">
                <button className="btn-neon px-8 py-3 text-sm flex items-center gap-2 mx-auto">
                  VIEW ALL GPU INSTANCES <ArrowRight size={14} />
                </button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="font-orbitron font-700 text-3xl lg:text-4xl text-white mb-4">
              Why Choose <span className="text-gradient">IndiaCloud?</span>
            </h2>
            <p className="font-outfit text-base text-gray-500 max-w-xl mx-auto" style={{ fontWeight: 300 }}>
              Built on cutting-edge infrastructure with India-first values.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feat) => (
              <div key={feat.title} className="glass rounded-2xl p-6 hover:-translate-y-1 transition-all duration-300">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: "rgba(0,243,255,0.1)", border: "1px solid rgba(0,243,255,0.2)" }}>
                  <feat.icon size={20} color="#00F3FF" />
                </div>
                <h3 className="font-rajdhani font-700 text-base text-white mb-2">{feat.title}</h3>
                <p className="font-outfit text-sm text-gray-500" style={{ fontWeight: 300 }}>{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20" style={{ background: "rgba(10,10,18,0.4)" }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-full text-xs font-rajdhani font-700 tracking-widest"
              style={{ background: "rgba(0,243,255,0.08)", border: "1px solid rgba(0,243,255,0.2)", color: "#00F3FF" }}>
              <Users size={12} /> TRUSTED BY THOUSANDS
            </div>
            <h2 className="font-orbitron font-700 text-3xl text-white">
              What Our <span className="text-gradient">Customers Say</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="testimonial-card">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(t.rating)].map((_, i) => (
                    <Star key={i} size={13} color="#ffcc00" fill="#ffcc00" />
                  ))}
                </div>
                <p className="font-outfit text-sm text-gray-400 leading-relaxed mb-4" style={{ fontWeight: 300 }}>
                  "{t.text}"
                </p>
                <div>
                  <div className="font-rajdhani font-700 text-sm text-white">{t.name}</div>
                  <div className="font-outfit text-xs text-gray-600">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div
            className="rounded-3xl p-12 relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, rgba(0,243,255,0.08), rgba(188,19,254,0.08))",
              border: "1px solid rgba(0,243,255,0.2)",
            }}
          >
            <div className="absolute inset-0 pointer-events-none"
              style={{ background: "radial-gradient(circle at 50% 0%, rgba(0,243,255,0.1) 0%, transparent 60%)" }} />
            <h2 className="font-orbitron font-900 text-3xl lg:text-4xl text-white mb-4 relative z-10">
              Ready to <span className="text-gradient">Launch?</span>
            </h2>
            <p className="font-outfit text-base text-gray-400 mb-8 max-w-xl mx-auto relative z-10" style={{ fontWeight: 300 }}>
              Join 50,000+ Indian businesses. Get started with a plan that scales with you.
              30-day money-back guarantee.
            </p>
            <div className="flex items-center justify-center gap-4 flex-wrap relative z-10">
              <Link to="/plan-recommender" className="no-underline">
                <button className="btn-primary flex items-center gap-2 px-8 py-3 text-sm font-700">
                  <Zap size={16} /> LET AI PICK MY PLAN
                </button>
              </Link>
              <Link to="/shared-hosting" className="no-underline">
                <button className="btn-neon flex items-center gap-2 px-8 py-3 text-sm">
                  VIEW ALL PLANS <ArrowRight size={14} />
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
